package com.arenaproplay.admin.pages.analytics

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import com.arenaproplay.admin.domain.admin.*
import com.arenaproplay.admin.ui.components.admin.*
import com.arenaproplay.admin.ui.components.admin.TrendPill
import com.arenaproplay.admin.ui.components.charts.svg.AreaChart
import com.arenaproplay.admin.ui.components.charts.svg.ChartSeries
import com.arenaproplay.admin.ui.components.charts.svg.RankedList
import com.arenaproplay.admin.ui.components.charts.svg.RankedRow
import com.arenaproplay.admin.ui.components.charts.svg.Donut
import com.arenaproplay.admin.ui.components.charts.svg.DonutSlice
import com.arenaproplay.admin.ui.components.charts.svg.FunnelBars
import com.arenaproplay.admin.ui.components.charts.svg.FunnelStep as SvgFunnelStep
import com.arenaproplay.admin.ui.theme.AppColors
import com.arenaproplay.admin.ui.theme.eyebrow
import com.arenaproplay.admin.ui.theme.numMedium
import com.arenaproplay.admin.ui.theme.bodySRegular
import com.arenaproplay.admin.ui.theme.bodySMedium
import com.arenaproplay.admin.ui.theme.bodyMRegular
import com.arenaproplay.admin.ui.theme.mono
import com.arenaproplay.admin.ui.utils.sBorderRadius
import com.arenaproplay.admin.ui.utils.sPadding
import com.arenaproplay.admin.ui.utils.countryFlag
import kotlinx.coroutines.async
import kotlinx.coroutines.coroutineScope
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import com.varabyte.kobweb.compose.ui.toAttrs
import org.jetbrains.compose.web.dom.Input
import kotlin.math.roundToInt

// ── Private helpers ───────────────────────────────────────────────────────────

private fun formatMinutesSeconds(minutes: Double): String {
    if (minutes.isNaN() || minutes.isInfinite() || minutes < 0) return "—"
    val totalSeconds = (minutes * 60).toInt()
    val m = totalSeconds / 60
    val s = totalSeconds % 60
    return "${m}m ${s}s"
}

private fun behFunnelStepLabel(key: String): String = when (key.lowercase()) {
    "splash"       -> "Splash"
    "onboarding_1" -> "Онб. 1/4"
    "onboarding_2" -> "Онб. 2/4"
    "onboarding_3" -> "Онб. 3/4"
    "onboarding_4" -> "Онб. 4/4"
    "welcome"      -> "Вход / Login"
    "arena"        -> "Главная"
    "feed"         -> "Лента"
    "registered"   -> "Зарегистрирован"
    else           -> key.replace('_', ' ').replaceFirstChar { it.uppercase() }
}

private fun platformColorHex(platform: String): String = when (platform.lowercase()) {
    "ios"     -> "#0BAB64"
    "android" -> "#C7D2FE"
    "web"     -> "#4D8CFF"
    else      -> "#6F727A"
}

private fun deviceLabel(platform: String, deviceType: String): String {
    val p = platform.lowercase()
    val d = deviceType.lowercase()
    return when {
        p == "ios"     -> "iOS"
        p == "android" -> "Android"
        p == "web" && d == "mobile"  -> "Web (mobile)"
        p == "web" && d == "tablet"  -> "Web (tablet)"
        p == "web" && d == "desktop" -> "Web (desktop)"
        p == "web"     -> "Web"
        else           -> "$platform $deviceType"
    }
}

private fun deviceColorHex(platform: String, deviceType: String): String = when {
    platform.lowercase() == "ios"                                              -> "#0BAB64"
    platform.lowercase() == "android"                                          -> "#C7D2FE"
    platform.lowercase() == "web" && deviceType.lowercase() == "mobile"       -> "#4D8CFF"
    platform.lowercase() == "web" && deviceType.lowercase() == "desktop"      -> "#8FB8FF"
    platform.lowercase() == "web" && deviceType.lowercase() == "tablet"       -> "#00E5A4"
    else                                                                        -> "#6F727A"
}

private fun statusBarColorHex(status: String): String = when (status.uppercase()) {
    "COMPLETED" -> "#00E5A4"
    "ONGOING"   -> "#4D8CFF"
    "NEW"       -> "#E4CD40"
    "CANCELLED" -> "#FF6B5B"
    else        -> "#6F727A"
}

private fun statusLabelRu(status: String): String = when (status.uppercase()) {
    "COMPLETED" -> "Завершено"
    "ONGOING"   -> "Активно"
    "NEW"       -> "Новое"
    "CANCELLED" -> "Отменено"
    else        -> status
}

private fun statusPillToneFromStr(status: String): PillTone = when (status.uppercase()) {
    "COMPLETED" -> PillTone.Success
    "ONGOING"   -> PillTone.Accent
    "NEW"       -> PillTone.Warning
    "CANCELLED" -> PillTone.Danger
    else        -> PillTone.Neutral
}

private fun formatHoursRu(h: Double): String {
    if (h <= 0.0) return "—"
    val r = kotlin.math.round(h * 10) / 10.0
    return if (r < 1.0) "${kotlin.math.round(h * 60).toInt()} мин"
    else if (r < 48.0) "$r ч"
    else "${kotlin.math.round(r / 24.0 * 10) / 10.0} д"
}

private fun fmtPct(v: Double): String {
    val r = kotlin.math.round(v * 10.0) / 10.0
    return if (r == r.toLong().toDouble()) "${r.toLong()}%" else "$r%"
}

private fun formatEpochMs(ms: Long): String {
    val sec = ms / 1000
    val h = (sec / 3600) % 24
    val m = (sec / 60) % 60
    val s = sec % 60
    val hStr = h.toString().padStart(2, '0')
    val mStr = m.toString().padStart(2, '0')
    val sStr = s.toString().padStart(2, '0')
    return "$hStr:$mStr:$sStr"
}

// ── Skeleton helper ───────────────────────────────────────────────────────────

@Composable
private fun SkeletonBox(height: Int = 24, modifier: Modifier = Modifier) {
    Box(
        modifier.fillMaxWidth().height(height.px)
            .sBorderRadius(6)
            .styleModifier {
                property("background", "rgba(255,255,255,.04)")
                property("animation", "admin-skeleton 1.6s ease-in-out infinite")
            }
    )
}

// ── Section error helper ──────────────────────────────────────────────────────

@Composable
private fun SectionError(message: String) {
    SpanText(
        "⚠ $message",
        Modifier.bodySRegular()
            .styleModifier { property("color", "#FF6B5B") }
    )
}

// ── Glass section title ───────────────────────────────────────────────────────

@Composable
private fun SectionTitle(eyebrowText: String, subtitle: String? = null) {
    SpanText(eyebrowText, Modifier.eyebrow().color(AppColors.Faint))
    if (subtitle != null) {
        Box(Modifier.height(4.px))
        SpanText(subtitle, Modifier.bodySRegular().color(AppColors.Faint))
    }
}

// ── Divider ───────────────────────────────────────────────────────────────────

@Composable
private fun GlassDivider() {
    Box(
        Modifier.fillMaxWidth().height(1.px)
            .styleModifier { property("background", "rgba(255,255,255,.08)") }
    )
}

// ── KPI chip ──────────────────────────────────────────────────────────────────

@Composable
private fun BehKpiChip(
    label: String,
    value: String?,
    accentHex: String,
    subCaption: String? = null,
    error: String? = null,
    deltaPercent: Double? = null,
    goodWhenUp: Boolean = true,
    modifier: Modifier = Modifier,
) {
    GlassCard(modifier = modifier, radius = 14, padding = 16) {
        SpanText(label, Modifier.eyebrow().color(AppColors.Faint))
        VSpace(8)
        when {
            error != null -> SectionError(error)
            value == null -> SkeletonBox(28)
            else -> {
                Row(
                    Modifier.styleModifier {
                        property("align-items", "baseline")
                        property("gap", "8px")
                        property("flex-wrap", "wrap")
                    }
                ) {
                    SpanText(
                        value,
                        Modifier.numMedium()
                            .styleModifier { property("color", accentHex) }
                    )
                    TrendPill(deltaPercent = deltaPercent, goodWhenUp = goodWhenUp)
                }
            }
        }
        if (subCaption != null && value != null && error == null) {
            VSpace(4)
            SpanText(
                subCaption,
                Modifier.bodySRegular()
                    .styleModifier { property("color", "rgba(240,244,252,.45)") }
            )
        }
    }
}

// ── Steps selector chips ──────────────────────────────────────────────────────

@Composable
private fun StepsSelector(
    current: Int,
    options: List<Int>,
    onSelect: (Int) -> Unit,
) {
    Row(
        Modifier.styleModifier {
            property("display", "flex")
            property("gap", "4px")
        }
    ) {
        options.forEach { n ->
            val active = n == current
            Box(
                Modifier
                    .sBorderRadius(8)
                    .sPadding(4, 10)
                    .styleModifier {
                        property("cursor", "pointer")
                        property("user-select", "none")
                        property("transition", "all 140ms ease")
                        if (active) {
                            property("background", "rgba(0,97,255,.28)")
                            property("color", "#8FB8FF")
                        } else {
                            property("background", "rgba(255,255,255,.05)")
                            property("color", "rgba(240,244,252,.50)")
                        }
                        property("font-size", "11px")
                        property("font-weight", if (active) "600" else "400")
                        property("font-family", "'Inter',sans-serif")
                    }
                    .classNames("na-press")
                    .onClick { onSelect(n) }
            ) {
                SpanText("$n", Modifier)
            }
        }
    }
}

// ── Session divider for timeline ──────────────────────────────────────────────

@Composable
private fun SessionDivider(sessionId: String?) {
    Row(
        Modifier.fillMaxWidth()
            .styleModifier {
                property("align-items", "center")
                property("gap", "10px")
                property("margin", "12px 0 8px")
            }
    ) {
        SpanText(
            "СЕССИЯ ${sessionId?.take(8) ?: "—"}",
            Modifier.eyebrow().color(AppColors.Faint)
                .styleModifier { property("white-space", "nowrap") }
        )
        Box(
            Modifier.weight(1f).height(1.px)
                .styleModifier { property("background", "rgba(255,255,255,.08)") }
        )
    }
}

// ── Platform chip ─────────────────────────────────────────────────────────────

@Composable
private fun PlatformChip(platform: String) {
    val color = platformColorHex(platform)
    Box(
        Modifier
            .sBorderRadius(6)
            .sPadding(2, 7)
            .styleModifier {
                property("background", "${color}22")
                property("display", "inline-flex")
                property("align-items", "center")
            }
    ) {
        SpanText(
            platform,
            Modifier.bodySMedium()
                .styleModifier {
                    property("color", color)
                    property("font-size", "11px")
                    property("font-weight", "600")
                }
        )
    }
}

// ── Retention cell ────────────────────────────────────────────────────────────

@Composable
private fun RetentionCell(value: Double?, isHeader: Boolean = false) {
    when {
        value == null -> {
            Box(
                Modifier.sBorderRadius(6)
                    .styleModifier {
                        property("background", "rgba(255,255,255,.04)")
                        property("width", "56px")
                        property("height", "32px")
                        property("display", "inline-flex")
                        property("align-items", "center")
                        property("justify-content", "center")
                    }
            ) {
                SpanText("—", Modifier.bodySRegular().color(AppColors.Faint))
            }
        }
        else -> {
            val alpha = (value * 0.7).coerceIn(0.0, 0.7)
            val pct = (value * 100).roundToInt()
            val textColor = if (pct >= 50) "#F0F4FC" else "rgba(240,244,252,.75)"
            Box(
                Modifier.sBorderRadius(6)
                    .styleModifier {
                        property("background", "rgba(0,229,164,${alpha})")
                        property("width", "56px")
                        property("height", "32px")
                        property("display", "inline-flex")
                        property("align-items", "center")
                        property("justify-content", "center")
                    }
            ) {
                SpanText(
                    "$pct%",
                    Modifier.bodySMedium()
                        .styleModifier {
                            property("color", textColor)
                            property("font-weight", if (pct >= 50) "700" else "500")
                            property("font-size", "11px")
                        }
                )
            }
        }
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// Main composable
// ─────────────────────────────────────────────────────────────────────────────

@Composable
fun BehaviorTabContent(
    period: Period,
    repo: com.arenaproplay.admin.domain.admin.AdminRepository,
) {
    // ── State declarations ────────────────────────────────────────────────────
    var kpis           by remember { mutableStateOf<BehaviorKpis?>(null) }
    var activeUsers    by remember { mutableStateOf<CategorizedTimeSeries?>(null) }
    var eventsSeries   by remember { mutableStateOf<CategorizedTimeSeries?>(null) }
    var topScreens     by remember { mutableStateOf<List<TopScreenRow>?>(null) }
    var topActions     by remember { mutableStateOf<List<TopActionRow>?>(null) }
    var devices        by remember { mutableStateOf<List<DeviceShareRow>?>(null) }
    var countries      by remember { mutableStateOf<List<CountryUsageRow>?>(null) }
    var newUserFunnel  by remember { mutableStateOf<NewUserFunnel?>(null) }
    var newUserActions by remember { mutableStateOf<List<TopActionRow>?>(null) }
    var journeys       by remember { mutableStateOf<List<JourneyPathRow>?>(null) }
    var cohorts        by remember { mutableStateOf<List<RetentionCohort>?>(null) }
    var signupFunnel   by remember { mutableStateOf<List<FunnelStep>?>(null) }
    var lifecycle      by remember { mutableStateOf<EventLifecycleStats?>(null) }
    var onboarding     by remember { mutableStateOf<OnboardingStats?>(null) }

    // Timeline
    var timelineUserId    by remember { mutableStateOf("") }
    var timelineInput     by remember { mutableStateOf("") }
    var timeline          by remember { mutableStateOf<List<TimelineEvent>?>(null) }
    var timelineLoading   by remember { mutableStateOf(false) }
    var timelineError     by remember { mutableStateOf<String?>(null) }

    // Journey steps selector
    var journeySteps by remember { mutableStateOf(5) }

    // Per-section errors
    var kpisError         by remember { mutableStateOf<String?>(null) }
    var activeUsersError  by remember { mutableStateOf<String?>(null) }
    var eventsSeriesError by remember { mutableStateOf<String?>(null) }
    var topScreensError   by remember { mutableStateOf<String?>(null) }
    var topActionsError   by remember { mutableStateOf<String?>(null) }
    var devicesError      by remember { mutableStateOf<String?>(null) }
    var countriesError    by remember { mutableStateOf<String?>(null) }
    var newUserFunnelError  by remember { mutableStateOf<String?>(null) }
    var newUserActionsError by remember { mutableStateOf<String?>(null) }
    var journeysError       by remember { mutableStateOf<String?>(null) }
    var cohortsError      by remember { mutableStateOf<String?>(null) }
    var signupFunnelError by remember { mutableStateOf<String?>(null) }
    var lifecycleError    by remember { mutableStateOf<String?>(null) }
    var onboardingError   by remember { mutableStateOf<String?>(null) }

    // ── Load period-dependent data ────────────────────────────────────────────
    LaunchedEffect(period) {
        kpis = null; activeUsers = null; eventsSeries = null
        topScreens = null; topActions = null; devices = null; countries = null
        newUserFunnel = null; newUserActions = null
        kpisError = null; activeUsersError = null; eventsSeriesError = null
        topScreensError = null; topActionsError = null; devicesError = null
        countriesError = null; newUserFunnelError = null; newUserActionsError = null

        coroutineScope {
            val dKpis = async {
                runCatching { repo.getBehaviorKpis(period) }
                    .onFailure { kpisError = it.message ?: "Ошибка загрузки" }
                    .getOrNull()
            }
            val dActiveUsers = async {
                runCatching { repo.getBehaviorActiveUsersSeries(period) }
                    .onFailure { activeUsersError = it.message ?: "Ошибка загрузки" }
                    .getOrNull()
            }
            val dEventsSeries = async {
                runCatching { repo.getBehaviorEventsSeries(period) }
                    .onFailure { eventsSeriesError = it.message ?: "Ошибка загрузки" }
                    .getOrNull()
            }
            val dTopScreens = async {
                runCatching { repo.getBehaviorTopScreens(period) }
                    .onFailure { topScreensError = it.message ?: "Ошибка загрузки" }
                    .getOrNull()
            }
            val dTopActions = async {
                runCatching { repo.getBehaviorTopActions(period) }
                    .onFailure { topActionsError = it.message ?: "Ошибка загрузки" }
                    .getOrNull()
            }
            val dDevices = async {
                runCatching { repo.getBehaviorDevices(period) }
                    .onFailure { devicesError = it.message ?: "Ошибка загрузки" }
                    .getOrNull()
            }
            val dCountries = async {
                runCatching { repo.getBehaviorCountries(period) }
                    .onFailure { countriesError = it.message ?: "Ошибка загрузки" }
                    .getOrNull()
            }
            val dNewUserFunnel = async {
                runCatching { repo.getNewUserFunnel(period) }
                    .onFailure { newUserFunnelError = it.message ?: "Ошибка загрузки" }
                    .getOrNull()
            }
            val dNewUserActions = async {
                runCatching { repo.getNewUserActions(period) }
                    .onFailure { newUserActionsError = it.message ?: "Ошибка загрузки" }
                    .getOrNull()
            }
            kpis           = dKpis.await()
            activeUsers    = dActiveUsers.await()
            eventsSeries   = dEventsSeries.await()
            topScreens     = dTopScreens.await()
            topActions     = dTopActions.await()
            devices        = dDevices.await()
            countries      = dCountries.await()
            newUserFunnel  = dNewUserFunnel.await()
            newUserActions = dNewUserActions.await()
        }
    }

    // ── Load period-independent data once ─────────────────────────────────────
    LaunchedEffect(Unit) {
        cohorts = null; signupFunnel = null; lifecycle = null; onboarding = null
        cohortsError = null; signupFunnelError = null; lifecycleError = null; onboardingError = null

        coroutineScope {
            val dCohorts = async {
                runCatching { repo.getRetentionCohorts(12) }
                    .onFailure { cohortsError = it.message ?: "Ошибка загрузки" }
                    .getOrNull()
            }
            val dSignupFunnel = async {
                runCatching { repo.getSignupFunnel() }
                    .onFailure { signupFunnelError = it.message ?: "Ошибка загрузки" }
                    .getOrNull()
            }
            val dLifecycle = async {
                runCatching { repo.getEventLifecycle() }
                    .onFailure { lifecycleError = it.message ?: "Ошибка загрузки" }
                    .getOrNull()
            }
            val dOnboarding = async {
                runCatching { repo.getOnboardingCompletion() }
                    .onFailure { onboardingError = it.message ?: "Ошибка загрузки" }
                    .getOrNull()
            }
            cohorts      = dCohorts.await()
            signupFunnel = dSignupFunnel.await()
            lifecycle    = dLifecycle.await()
            onboarding   = dOnboarding.await()
        }
    }

    // ── Re-load journeys when steps changes ───────────────────────────────────
    LaunchedEffect(period, journeySteps) {
        journeys = null
        journeysError = null
        runCatching { repo.getBehaviorFirstSteps(period, journeySteps) }
            .onFailure { journeysError = it.message ?: "Ошибка загрузки" }
            .getOrNull()
            ?.also { journeys = it }
    }

    // ── Coroutine scope for timeline on-demand load ───────────────────────────
    val scope = rememberCoroutineScope()

    // ─────────────────────────────────────────────────────────────────────────
    // Render
    // ─────────────────────────────────────────────────────────────────────────

    Column(Modifier.fillMaxWidth()) {

        // ── 0. Сессии — most-wanted view, shown first ─────────────────────────
        SessionListSection(period = period, repo = repo)

        VSpace(14)

        // ── 1. KPI chips row ──────────────────────────────────────────────────
        // Bug-class 1 fix: minmax(0,1fr) prevents KPI label/value overflow
        Div(attrs = Modifier.fillMaxWidth()
                .styleModifier {
                    property("display", "grid")
                    property("grid-template-columns", "repeat(4, minmax(0,1fr))")
                    property("gap", "12px")
                }.toAttrs()
        ) {
            BehKpiChip(
                label        = "АКТИВНЫХ ЮЗЕРОВ",
                value        = kpis?.activeUsers?.current?.let { formatNumber(it) },
                accentHex    = "#0061FF",
                error        = kpisError,
                deltaPercent = kpis?.activeUsers?.percentDelta,
            )
            BehKpiChip(
                label        = "СЕССИИ",
                value        = kpis?.sessions?.current?.let { formatNumber(it) },
                accentHex    = "#00E5A4",
                error        = kpisError,
                deltaPercent = kpis?.sessions?.percentDelta,
            )
            BehKpiChip(
                label        = "СР. СЕССИЯ",
                value        = kpis?.avgSessionMinutes?.current?.let { formatMinutesSeconds(it) },
                accentHex    = "#E4CD40",
                error        = kpisError,
                deltaPercent = kpis?.avgSessionMinutes?.percentDelta,
            )
            BehKpiChip(
                label        = "СОБЫТИЯ",
                value        = kpis?.totalEvents?.current?.let { formatNumber(it) },
                accentHex    = "#4D8CFF",
                error        = kpisError,
                deltaPercent = kpis?.totalEvents?.percentDelta,
            )
        }

        VSpace(14)

        // ── 2. Activity area charts ────────────────────────────────────────────
        GlassCard(radius = 16, padding = 20) {
            SectionTitle("АКТИВНОСТЬ", "Уникальные пользователи · по платформам")
            VSpace(14)
            when {
                activeUsersError != null -> SectionError(activeUsersError!!)
                activeUsers == null      -> SkeletonBox(200)
                else -> {
                    val labels = activeUsers!!.labels
                    val series = activeUsers!!.series.map { (name, values) ->
                        ChartSeries(
                            name     = name,
                            values   = values,
                            colorHex = platformColorHex(name),
                        )
                    }
                    AreaChart(series = series, labels = labels, heightPx = 200)
                }
            }

            VSpace(18)
            GlassDivider()
            VSpace(18)

            SectionTitle("СОБЫТИЯ")
            VSpace(14)
            when {
                eventsSeriesError != null -> SectionError(eventsSeriesError!!)
                eventsSeries == null      -> SkeletonBox(140)
                else -> {
                    val labels = eventsSeries!!.labels
                    val series = eventsSeries!!.series.map { (name, values) ->
                        ChartSeries(
                            name     = name,
                            values   = values,
                            colorHex = platformColorHex(name),
                        )
                    }
                    AreaChart(series = series, labels = labels, heightPx = 140)
                }
            }
        }

        VSpace(14)

        // ── 3. Top screens + Top actions (two columns) ─────────────────────────
        // Bug-class 1 fix: minmax(0,1fr) so RankedList labels don't blow out tracks
        Div(attrs = Modifier.fillMaxWidth()
                .styleModifier {
                    property("display", "grid")
                    property("grid-template-columns", "minmax(0,1fr) minmax(0,1fr)")
                    property("gap", "12px")
                    property("align-items", "start")
                }.toAttrs()
        ) {
            // Top screens
            GlassCard(radius = 16, padding = 20) {
                SectionTitle("ТОП ЭКРАНОВ")
                VSpace(14)
                when {
                    topScreensError != null -> SectionError(topScreensError!!)
                    topScreens == null -> SkeletonBox(180)
                    topScreens!!.isEmpty() -> SpanText("Нет данных", Modifier.bodySRegular().color(AppColors.Faint))
                    else -> {
                        val maxViews = topScreens!!.maxOfOrNull { it.views }?.coerceAtLeast(1L) ?: 1L
                        val rows = topScreens!!.take(10).map { s ->
                            RankedRow(
                                label    = s.screen,
                                value    = formatNumber(s.views),
                                fraction = s.views.toDouble() / maxViews.toDouble(),
                            )
                        }
                        RankedList(rows = rows, accentHex = "#4D8CFF")
                    }
                }
            }

            // Top actions
            GlassCard(radius = 16, padding = 20) {
                SectionTitle("ТОП ДЕЙСТВИЙ")
                VSpace(14)
                when {
                    topActionsError != null -> SectionError(topActionsError!!)
                    topActions == null -> SkeletonBox(180)
                    topActions!!.isEmpty() -> SpanText("Нет данных", Modifier.bodySRegular().color(AppColors.Faint))
                    else -> {
                        val maxCount = topActions!!.maxOfOrNull { it.count }?.coerceAtLeast(1L) ?: 1L
                        val rows = topActions!!.take(10).map { a ->
                            RankedRow(
                                label    = a.action.removePrefix("click."),
                                value    = formatNumber(a.count),
                                fraction = a.count.toDouble() / maxCount.toDouble(),
                            )
                        }
                        RankedList(rows = rows, accentHex = "#00E5A4")
                    }
                }
            }
        }

        VSpace(14)

        // ── 4. Devices donut + Countries ──────────────────────────────────────
        // Bug-class 1 fix: minmax(0,Xfr) so donut legend text can't blow grid tracks
        Div(attrs = Modifier.fillMaxWidth()
                .styleModifier {
                    property("display", "grid")
                    property("grid-template-columns", "minmax(0,1.2fr) minmax(0,1fr)")
                    property("gap", "12px")
                    property("align-items", "start")
                }.toAttrs()
        ) {
            // Devices donut
            GlassCard(radius = 16, padding = 20) {
                SectionTitle("УСТРОЙСТВА")
                VSpace(14)
                when {
                    devicesError != null -> SectionError(devicesError!!)
                    devices == null -> SkeletonBox(120)
                    devices!!.isEmpty() -> SpanText("Нет данных", Modifier.bodySRegular().color(AppColors.Faint))
                    else -> {
                        val slices = devices!!.map { d ->
                            DonutSlice(
                                name     = deviceLabel(d.platform, d.deviceType),
                                value    = d.users.toDouble(),
                                colorHex = deviceColorHex(d.platform, d.deviceType),
                            )
                        }
                        Row(
                            Modifier.fillMaxWidth()
                                .styleModifier {
                                    property("align-items", "center")
                                    property("gap", "20px")
                                }
                        ) {
                            Donut(slices = slices, sizePx = 120, strokeWidth = 14)
                            // Bug-class 2 fix: min-width:0 on flex child so legend text
                            // can truncate rather than overflow the row
                            Column(Modifier.styleModifier { property("gap", "8px"); property("min-width", "0"); property("flex", "1") }) {
                                val total = devices!!.sumOf { it.users }.coerceAtLeast(1L)
                                devices!!.forEach { d ->
                                    val hex = deviceColorHex(d.platform, d.deviceType)
                                    val lbl = deviceLabel(d.platform, d.deviceType)
                                    val pct = (d.users.toDouble() / total * 100).roundToInt()
                                    Row(
                                        Modifier.styleModifier {
                                            property("align-items", "center")
                                            property("gap", "8px")
                                        }
                                    ) {
                                        Box(
                                            Modifier.width(8.px).height(8.px)
                                                .sBorderRadius(2)
                                                .styleModifier { property("background", hex) }
                                        )
                                        SpanText(
                                            lbl,
                                            Modifier.bodySRegular().color(AppColors.Ink)
                                                .styleModifier {
                                                    // Bug-class 2 fix: overflow containment in flex row
                                                    property("overflow", "hidden")
                                                    property("text-overflow", "ellipsis")
                                                    property("white-space", "nowrap")
                                                }
                                        )
                                        SpanText("$pct%", Modifier.bodySMedium().color(AppColors.Faint))
                                    }
                                }
                            }
                        }
                    }
                }
            }

            // Countries
            GlassCard(radius = 16, padding = 20) {
                SectionTitle("СТРАНЫ")
                VSpace(14)
                when {
                    countriesError != null -> SectionError(countriesError!!)
                    countries == null -> SkeletonBox(160)
                    countries!!.isEmpty() -> SpanText("Нет данных", Modifier.bodySRegular().color(AppColors.Faint))
                    else -> {
                        val maxUsers = countries!!.maxOfOrNull { it.users }?.coerceAtLeast(1L) ?: 1L
                        countries!!.take(6).forEachIndexed { idx, c ->
                            val fraction = c.users.toDouble() / maxUsers.toDouble()
                            Column(Modifier.fillMaxWidth()) {
                                Row(
                                    Modifier.fillMaxWidth()
                                        .styleModifier {
                                            property("align-items", "center")
                                            property("gap", "8px")
                                            property("margin-bottom", "4px")
                                        }
                                ) {
                                    SpanText(
                                        countryFlag(c.country),
                                        Modifier.styleModifier { property("font-size", "14px") }
                                    )
                                    SpanText(
                                        c.country,
                                        Modifier.bodySRegular().color(AppColors.Ink)
                                            .styleModifier { property("flex", "1") }
                                    )
                                    SpanText(
                                        formatNumber(c.users),
                                        Modifier.bodySMedium().color(AppColors.Faint)
                                    )
                                }
                                // Proportional bar
                                Box(
                                    Modifier.fillMaxWidth().height(4.px).sBorderRadius(3)
                                        .styleModifier { property("background", "rgba(255,255,255,.06)") }
                                ) {
                                    Box(
                                        Modifier.height(4.px).sBorderRadius(3)
                                            .styleModifier {
                                                property("width", "${(fraction * 100).coerceIn(2.0, 100.0)}%")
                                                property("background", "#00E5A4")
                                            }
                                    )
                                }
                            }
                            if (idx < 5) VSpace(10)
                        }
                    }
                }
            }
        }

        VSpace(14)

        // ── 5. New-user funnel ────────────────────────────────────────────────
        GlassCard(radius = 16, padding = 20) {
            SectionTitle("НОВЫЕ ПОЛЬЗОВАТЕЛИ", "Новые устройства — первая сессия · воронка онбординга")
            VSpace(14)
            when {
                newUserFunnelError != null -> SectionError(newUserFunnelError!!)
                newUserFunnel == null -> SkeletonBox(200)
                newUserFunnel!!.steps.isEmpty() -> SpanText("Нет данных", Modifier.bodySRegular().color(AppColors.Faint))
                else -> {
                    val funnel = newUserFunnel!!
                    // Headline: total installs
                    SpanText(
                        "${formatNumber(funnel.installs)} новых устройств",
                        Modifier.styleModifier {
                            property("font-family", "'Unbounded', sans-serif")
                            property("font-size", "20px")
                            property("font-weight", "700")
                            property("color", "#F0F4FC")
                            property("letter-spacing", "-0.3px")
                        }
                    )
                    VSpace(12)
                    val svgSteps = funnel.steps.map { step ->
                        SvgFunnelStep(
                            label = behFunnelStepLabel(step.key),
                            value = step.devices,
                        )
                    }
                    FunnelBars(steps = svgSteps)
                    // Per-step first-session sub-lines
                    val hasFirstSession = funnel.steps.any { it.firstSessionDevices > 0 }
                    if (hasFirstSession) {
                        VSpace(10)
                        Column(Modifier.fillMaxWidth().styleModifier { property("gap", "2px") }) {
                            funnel.steps.forEach { step ->
                                if (step.firstSessionDevices > 0) {
                                    Row(
                                        Modifier.styleModifier {
                                            property("align-items", "center")
                                            property("gap", "6px")
                                        }
                                    ) {
                                        SpanText(
                                            behFunnelStepLabel(step.key),
                                            Modifier.bodySRegular()
                                                .styleModifier { property("color", "rgba(240,244,252,.55)") }
                                        )
                                        SpanText(
                                            "· в т.ч. первая сессия: ${formatNumber(step.firstSessionDevices)}",
                                            Modifier.bodySRegular()
                                                .styleModifier { property("color", "#00E5A4") }
                                        )
                                    }
                                }
                            }
                        }
                    }
                }
            }

            // Signups by provider pills
            val providerMap = newUserFunnel?.signupsByProvider
            if (!providerMap.isNullOrEmpty()) {
                VSpace(16)
                GlassDivider()
                VSpace(12)
                SpanText("РЕГИСТРАЦИИ ПО ПРОВАЙДЕРУ", Modifier.eyebrow().color(AppColors.Faint))
                VSpace(10)
                Row(
                    Modifier.styleModifier {
                        property("display", "flex")
                        property("flex-wrap", "wrap")
                        property("gap", "8px")
                    }
                ) {
                    providerMap.entries.sortedByDescending { it.value }.forEach { (provider, count) ->
                        Box(
                            Modifier
                                .sBorderRadius(999)
                                .sPadding(4, 12)
                                .styleModifier {
                                    property("background", "rgba(255,255,255,.06)")
                                    property("border", "1px solid rgba(255,255,255,.08)")
                                    property("display", "inline-flex")
                                    property("align-items", "center")
                                    property("gap", "6px")
                                }
                        ) {
                            SpanText(
                                provider,
                                Modifier.bodySMedium().color(AppColors.Ink)
                            )
                            SpanText(
                                formatNumber(count),
                                Modifier.bodySMedium()
                                    .styleModifier { property("color", "#4D8CFF") }
                            )
                        }
                    }
                }
            }
        }

        VSpace(14)

        // ── 5b. Первые шаги (new-user actions table) ─────────────────────────
        GlassCard(radius = 16, padding = 20) {
            SectionTitle("ПЕРВЫЕ ШАГИ", "Действия новых юзеров в первой сессии")
            VSpace(14)
            when {
                newUserActionsError != null -> SectionError(newUserActionsError!!)
                newUserActions == null -> SkeletonBox(180)
                newUserActions!!.isEmpty() -> EmptyState(
                    icon  = "👣",
                    title = "Нет данных",
                    hint  = "Действия новых пользователей за период не зафиксированы",
                )
                else -> {
                    // Table header
                    Row(
                        Modifier.fillMaxWidth()
                            .sPadding(4, 0)
                            .styleModifier {
                                property("border-bottom", "1px solid rgba(255,255,255,.08)")
                                property("padding-bottom", "8px")
                                property("margin-bottom", "4px")
                                property("gap", "8px")
                            }
                    ) {
                        Box(Modifier.styleModifier { property("flex", "1") }) {
                            SpanText("ДЕЙСТВИЕ", Modifier.eyebrow().color(AppColors.Faint))
                        }
                        Box(Modifier.width(90.px)) {
                            SpanText("КОЛ-ВО", Modifier.eyebrow().color(AppColors.Faint))
                        }
                        Box(Modifier.width(80.px)) {
                            SpanText("УСТРОЙСТВ", Modifier.eyebrow().color(AppColors.Faint))
                        }
                    }
                    newUserActions!!.forEachIndexed { idx, action ->
                        Row(
                            Modifier.fillMaxWidth()
                                .sPadding(8, 10)
                                .sBorderRadius(8)
                                .styleModifier {
                                    if (idx != newUserActions!!.size - 1)
                                        property("border-bottom", "1px solid rgba(255,255,255,.05)")
                                    property("align-items", "center")
                                    property("gap", "8px")
                                }
                        ) {
                            SpanText(
                                action.action.removePrefix("click."),
                                Modifier.bodySRegular().color(AppColors.Ink)
                                    .styleModifier {
                                        property("flex", "1")
                                        property("overflow", "hidden")
                                        property("text-overflow", "ellipsis")
                                        property("white-space", "nowrap")
                                    }
                            )
                            Box(Modifier.width(90.px)) {
                                SpanText(
                                    formatNumber(action.count),
                                    Modifier.bodySMedium()
                                        .styleModifier { property("color", "#4D8CFF") }
                                )
                            }
                            Box(Modifier.width(80.px)) {
                                SpanText(
                                    formatNumber(action.uniqueUsers),
                                    Modifier.bodySRegular().color(AppColors.Faint)
                                )
                            }
                        }
                    }
                }
            }
        }

        VSpace(14)

        // ── 6. Journeys ───────────────────────────────────────────────────────
        GlassCard(radius = 16, padding = 20) {
            Row(
                Modifier.fillMaxWidth()
                    .styleModifier {
                        property("align-items", "center")
                        property("justify-content", "space-between")
                    }
            ) {
                SectionTitle("ПУТЬ НОВОГО ЮЗЕРА")
                StepsSelector(
                    current  = journeySteps,
                    options  = listOf(3, 5, 7),
                    onSelect = { journeySteps = it },
                )
            }
            VSpace(14)
            when {
                journeysError != null -> SectionError(journeysError!!)
                journeys == null -> SkeletonBox(160)
                journeys!!.isEmpty() -> SpanText("Нет данных", Modifier.bodySRegular().color(AppColors.Faint))
                else -> {
                    Column(Modifier.fillMaxWidth()) {
                        journeys!!.forEachIndexed { idx, row ->
                            Row(
                                Modifier.fillMaxWidth()
                                    .sPadding(10, 12)
                                    .sBorderRadius(8)
                                    .styleModifier {
                                        if (idx != journeys!!.size - 1)
                                            property("border-bottom", "1px solid rgba(255,255,255,.05)")
                                        property("align-items", "center")
                                        property("justify-content", "space-between")
                                        property("gap", "12px")
                                    }
                            ) {
                                SpanText(
                                    row.path,
                                    Modifier.mono(11)
                                        .styleModifier {
                                            property("color", "rgba(240,244,252,.80)")
                                            property("flex", "1")
                                            property("overflow", "hidden")
                                            property("text-overflow", "ellipsis")
                                            property("white-space", "nowrap")
                                        }
                                )
                                Box(
                                    Modifier.sBorderRadius(999)
                                        .sPadding(3, 10)
                                        .styleModifier {
                                            property("background", "rgba(0,97,255,.22)")
                                            property("display", "inline-flex")
                                            property("align-items", "center")
                                        }
                                ) {
                                    SpanText(
                                        formatNumber(row.count),
                                        Modifier.bodySMedium()
                                            .styleModifier {
                                                property("color", "#8FB8FF")
                                                property("font-weight", "600")
                                                property("white-space", "nowrap")
                                            }
                                    )
                                }
                            }
                        }
                    }
                }
            }
        }

        VSpace(14)

        // ── 7. Retention cohorts heatmap ──────────────────────────────────────
        GlassCard(radius = 16, padding = 20) {
            SectionTitle(
                "УДЕРЖАНИЕ ПО КОГОРТАМ",
                "% вернувшихся на день D после регистрации"
            )
            VSpace(14)
            when {
                cohortsError != null -> SectionError(cohortsError!!)
                cohorts == null -> SkeletonBox(300)
                cohorts!!.isEmpty() -> SpanText("Нет данных", Modifier.bodySRegular().color(AppColors.Faint))
                else -> {
                    // Header row
                    Row(
                        Modifier.fillMaxWidth()
                            .styleModifier {
                                property("align-items", "center")
                                property("gap", "8px")
                                property("margin-bottom", "8px")
                                property("padding", "0 4px")
                            }
                    ) {
                        Box(Modifier.styleModifier { property("flex", "1") }) {
                            SpanText("Когорта", Modifier.eyebrow().color(AppColors.Faint))
                        }
                        Box(Modifier.width(60.px)) {
                            SpanText("Размер", Modifier.eyebrow().color(AppColors.Faint))
                        }
                        Box(Modifier.width(60.px)) {
                            SpanText("D1", Modifier.eyebrow().color(AppColors.Faint))
                        }
                        Box(Modifier.width(60.px)) {
                            SpanText("D7", Modifier.eyebrow().color(AppColors.Faint))
                        }
                        Box(Modifier.width(60.px)) {
                            SpanText("D30", Modifier.eyebrow().color(AppColors.Faint))
                        }
                    }
                    GlassDivider()
                    VSpace(8)

                    cohorts!!.take(12).forEachIndexed { idx, cohort ->
                        Row(
                            Modifier.fillMaxWidth()
                                .sPadding(6, 4)
                                .sBorderRadius(8)
                                .styleModifier {
                                    property("align-items", "center")
                                    property("gap", "8px")
                                }
                        ) {
                            Box(Modifier.styleModifier { property("flex", "1") }) {
                                SpanText(
                                    cohort.cohortStart.take(10),
                                    Modifier.bodySRegular().color(AppColors.Ink)
                                )
                            }
                            Box(Modifier.width(60.px)) {
                                SpanText(
                                    formatNumber(cohort.cohortSize.toLong()),
                                    Modifier.bodySMedium().color(AppColors.Muted)
                                )
                            }
                            Box(Modifier.width(60.px)) {
                                RetentionCell(cohort.d1)
                            }
                            Box(Modifier.width(60.px)) {
                                RetentionCell(cohort.d7)
                            }
                            Box(Modifier.width(60.px)) {
                                RetentionCell(cohort.d30)
                            }
                        }
                        if (idx < cohorts!!.take(12).size - 1) VSpace(4)
                    }
                }
            }
        }

        VSpace(14)

        // ── 8. Event lifecycle KPIs ────────────────────────────────────────────
        GlassCard(radius = 16, padding = 20) {
            SectionTitle("ЖИЗНЕННЫЙ ЦИКЛ СОБЫТИЙ")
            VSpace(14)
            when {
                lifecycleError != null -> SectionError(lifecycleError!!)
                lifecycle == null -> SkeletonBox(180)
                else -> {
                    val lc = lifecycle!!
                    // 5 KPI chips — Bug-class 1 fix: minmax(0,1fr)
                    Div(attrs = Modifier.fillMaxWidth()
                            .styleModifier {
                                property("display", "grid")
                                property("grid-template-columns", "repeat(5, minmax(0,1fr))")
                                property("gap", "10px")
                            }.toAttrs()
                    ) {
                        BehKpiChip(
                            label     = "ВСЕГО",
                            value     = formatNumber(lc.totalEvents),
                            accentHex = "#F0F4FC",
                        )
                        BehKpiChip(
                            label     = "ЗАВЕРШАЕМОСТЬ",
                            value     = fmtPct(lc.completionRate * 100),
                            accentHex = "#00E5A4",
                        )
                        BehKpiChip(
                            label     = "ОТМЕНЯЕМОСТЬ",
                            value     = fmtPct(lc.cancellationRate * 100),
                            accentHex = "#FF6B5B",
                        )
                        BehKpiChip(
                            label     = "ВРЕМЯ ДО",
                            value     = formatHoursRu(lc.avgLeadTimeHours),
                            accentHex = "#4D8CFF",
                        )
                        BehKpiChip(
                            label     = "ЗАПОЛНЯЕМОСТЬ",
                            value     = fmtPct(lc.avgFillRate * 100),
                            accentHex = "#00E5A4",
                        )
                    }

                    VSpace(18)
                    SpanText("РАЗБИВКА ПО СТАТУСАМ", Modifier.eyebrow().color(AppColors.Faint))
                    VSpace(10)

                    val total = lc.statusBreakdown.values.sum().coerceAtLeast(1L)
                    lc.statusBreakdown.entries
                        .sortedByDescending { it.value }
                        .forEachIndexed { idx, (status, count) ->
                            val pct = count.toDouble() / total * 100.0
                            val barColor = statusBarColorHex(status)
                            Row(
                                Modifier.fillMaxWidth(),
                                verticalAlignment = Alignment.CenterVertically,
                            ) {
                                Box(Modifier.width(130.px)) {
                                    Pill(statusLabelRu(status), statusPillToneFromStr(status))
                                }
                                Box(
                                    Modifier.weight(1f).height(22.px).sBorderRadius(6)
                                        .styleModifier {
                                            property("background", "rgba(255,255,255,.06)")
                                            property("overflow", "hidden")
                                        }
                                ) {
                                    Box(
                                        Modifier.height(22.px).sBorderRadius(6)
                                            .styleModifier {
                                                property("width", "${pct.coerceIn(2.0, 100.0)}%")
                                                property("background", barColor)
                                            }
                                    )
                                }
                                Box(Modifier.width(12.px))
                                Box(Modifier.width(110.px)) {
                                    SpanText(
                                        "$count · ${fmtPct(pct)}",
                                        Modifier.bodySMedium().color(AppColors.Ink)
                                            .styleModifier { property("font-weight", "600") }
                                    )
                                }
                            }
                            if (idx != lc.statusBreakdown.size - 1) VSpace(8)
                        }
                }
            }
        }

        VSpace(14)

        // ── 9+10. Signup funnel + Onboarding side-by-side ─────────────────────
        // Bug-class 1 fix: minmax(0,1fr) so funnel bars and onboarding labels
        // don't blow out the grid at narrow widths
        Div(attrs = Modifier.fillMaxWidth()
                .styleModifier {
                    property("display", "grid")
                    property("grid-template-columns", "minmax(0,1fr) minmax(0,1fr)")
                    property("gap", "12px")
                    property("align-items", "start")
                }.toAttrs()
        ) {
            // Signup funnel
            GlassCard(radius = 16, padding = 20) {
                SectionTitle("ВОРОНКА РЕГИСТРАЦИИ", "Уникальные пользователи по шагам")
                VSpace(14)
                when {
                    signupFunnelError != null -> SectionError(signupFunnelError!!)
                    signupFunnel == null -> SkeletonBox(200)
                    signupFunnel!!.isEmpty() -> SpanText("Нет данных", Modifier.bodySRegular().color(AppColors.Faint))
                    else -> {
                        val svgSteps = signupFunnel!!.map { step ->
                            SvgFunnelStep(
                                label = step.label,
                                value = step.userCount,
                            )
                        }
                        FunnelBars(steps = svgSteps)
                    }
                }
            }

            // Onboarding
            GlassCard(radius = 16, padding = 20) {
                SectionTitle("ОНБОРДИНГ", "% активных юзеров, завершивших каждый шаг")
                VSpace(14)
                when {
                    onboardingError != null -> SectionError(onboardingError!!)
                    onboarding == null -> SkeletonBox(200)
                    onboarding!!.steps.isEmpty() -> SpanText("Нет данных", Modifier.bodySRegular().color(AppColors.Faint))
                    else -> {
                        val ob = onboarding!!
                        SpanText(
                            "На основе ${formatNumber(ob.totalActiveUsers)} пользователей",
                            Modifier.bodySRegular().color(AppColors.Faint),
                        )
                        VSpace(12)
                        val total = ob.totalActiveUsers.coerceAtLeast(1L)
                        ob.steps.forEachIndexed { idx, step ->
                            val pct = step.completed.toDouble() / total * 100.0
                            val (barColor, pillTone) = when {
                                pct >= 70 -> "#00E5A4" to PillTone.Success
                                pct >= 40 -> "#4D8CFF" to PillTone.Accent
                                pct >= 20 -> "#E4CD40" to PillTone.Warning
                                else      -> "#FF6B5B" to PillTone.Danger
                            }
                            Row(
                                Modifier.fillMaxWidth(),
                                verticalAlignment = Alignment.CenterVertically,
                            ) {
                                Box(Modifier.width(160.px)) {
                                    SpanText(step.label, Modifier.bodyMRegular().color(AppColors.Ink))
                                }
                                // Bar track
                                Box(
                                    Modifier.weight(1f).height(8.px).sBorderRadius(4)
                                        .styleModifier {
                                            property("background", "${barColor}2E")
                                            property("overflow", "hidden")
                                        }
                                ) {
                                    Box(
                                        Modifier.height(8.px).sBorderRadius(4)
                                            .styleModifier {
                                                property("width", "${pct.coerceIn(2.0, 100.0)}%")
                                                property("background", barColor)
                                            }
                                    )
                                }
                                Box(Modifier.width(12.px))
                                Box(Modifier.width(120.px)) {
                                    Pill(
                                        text = "${step.completed} · ${fmtPct(pct)}",
                                        tone = pillTone,
                                    )
                                }
                            }
                            if (idx != ob.steps.size - 1) VSpace(8)
                        }
                    }
                }
            }
        }

        VSpace(14)

        // ── 11. Timeline explorer ─────────────────────────────────────────────
        GlassCard(radius = 16, padding = 20) {
            SectionTitle("ТАЙМЛАЙН ЮЗЕРА", "Вставьте UUID пользователя и загрузите историю событий")
            VSpace(14)

            // Input row
            Row(
                Modifier.fillMaxWidth()
                    .styleModifier {
                        property("align-items", "center")
                        property("gap", "10px")
                    }
            ) {
                // UUID input
                Box(Modifier.weight(1f)) {
                    Input(
                        type = InputType.Text,
                        attrs = {
                            value(timelineInput)
                            onInput { e -> timelineInput = e.value }
                            placeholder("UUID пользователя")
                            style {
                                property("width", "100%")
                                property("background", "rgba(255,255,255,.05)")
                                property("border", "1px solid rgba(255,255,255,.08)")
                                property("border-radius", "10px")
                                property("padding", "10px 14px")
                                property("color", "#F0F4FC")
                                property("font-family", "'JetBrains Mono',monospace")
                                property("font-size", "13px")
                                property("outline", "none")
                                property("box-sizing", "border-box")
                            }
                        }
                    )
                }
                // Load button
                Box(
                    Modifier
                        .sBorderRadius(10)
                        .sPadding(10, 18)
                        .styleModifier {
                            property("background", "rgba(0,97,255,.28)")
                            property("border", "1px solid rgba(0,97,255,.45)")
                            property("cursor", if (timelineLoading) "default" else "pointer")
                            property("user-select", "none")
                            property("transition", "all 140ms ease")
                            property("white-space", "nowrap")
                        }
                        .classNames("na-press")
                        .onClick {
                            val uid = timelineInput.trim()
                            if (uid.isNotBlank() && !timelineLoading) {
                                scope.launch {
                                    timelineLoading = true
                                    timelineError = null
                                    timeline = null
                                    runCatching { repo.getUserTimeline(uid) }
                                        .onFailure { timelineError = it.message ?: "Ошибка загрузки" }
                                        .getOrNull()
                                        ?.also {
                                            timeline = it
                                            timelineUserId = uid
                                        }
                                    timelineLoading = false
                                }
                            }
                        }
                ) {
                    SpanText(
                        if (timelineLoading) "Загрузка..." else "Загрузить",
                        Modifier.bodySMedium()
                            .styleModifier {
                                property("color", "#8FB8FF")
                                property("font-weight", "600")
                            }
                    )
                }
            }

            // Error
            if (timelineError != null) {
                VSpace(10)
                SectionError(timelineError!!)
            }

            // Timeline events
            val tl = timeline
            if (tl != null) {
                VSpace(16)
                GlassDivider()

                if (tl.isEmpty()) {
                    VSpace(12)
                    SpanText("Нет событий для этого пользователя", Modifier.bodySRegular().color(AppColors.Faint))
                } else {
                    // Group by sessionId for session dividers
                    var lastSession: String? = null
                    tl.forEach { event ->
                        val sessionChanged = event.sessionId != lastSession
                        if (sessionChanged) {
                            SessionDivider(event.sessionId)
                            lastSession = event.sessionId
                        }
                        TimelineEventRow(event)
                    }
                }
            }
        }

        VSpace(40)
    }
}

// ── Timeline event row ────────────────────────────────────────────────────────

@Composable
private fun TimelineEventRow(event: TimelineEvent) {
    Row(
        Modifier.fillMaxWidth()
            .sPadding(8, 12)
            .sBorderRadius(8)
            .styleModifier {
                property("align-items", "flex-start")
                property("gap", "10px")
                property("border-bottom", "1px solid rgba(255,255,255,.04)")
            }
    ) {
        // Timestamp
        SpanText(
            formatEpochMs(event.occurredAt),
            Modifier.mono(10)
                .styleModifier {
                    property("color", "rgba(240,244,252,.45)")
                    property("white-space", "nowrap")
                    property("flex-shrink", "0")
                    property("min-width", "68px")
                }
        )

        Column(Modifier.weight(1f).styleModifier { property("gap", "4px") }) {
            // Event type + screen + platform
            Row(
                Modifier.styleModifier {
                    property("align-items", "center")
                    property("gap", "8px")
                    property("flex-wrap", "wrap")
                }
            ) {
                SpanText(
                    event.eventType,
                    Modifier.bodySMedium().color(AppColors.Ink)
                        .styleModifier { property("font-weight", "600") }
                )
                if (event.screen != null) {
                    SpanText(
                        event.screen,
                        Modifier.bodySRegular()
                            .styleModifier { property("color", "#00E5A4") }
                    )
                }
                PlatformChip(event.platform)
            }
            // Metadata
            if (!event.metadata.isNullOrBlank()) {
                SpanText(
                    event.metadata,
                    Modifier.mono(11)
                        .styleModifier { property("color", "rgba(240,244,252,.55)") }
                )
            }
        }
    }
}
